import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Lang, STRINGS } from './strings';
import { I18nContext, I18nCtx } from './context';

// The chosen language is kept in this browser only (localStorage) so a
// returning visitor keeps their choice. It is never sent anywhere and is not
// an identifier — every visitor of the same language stores the same value.
const KEY = 'zencera:lang';
const DEFAULT_LANG: Lang = 'en';

function readStored(): Lang {
  try {
    const v = localStorage.getItem(KEY);
    if (v && v in STRINGS) return v as Lang;
  } catch {
    /* storage blocked: fall back to default */
  }
  return DEFAULT_LANG;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(readStored);

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try {
      localStorage.setItem(KEY, l);
    } catch {
      /* ignore */
    }
  }, []);

  // WCAG 3.1.1: the page language must match the content.
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo<I18nCtx>(() => {
    const locale = lang === 'en' ? 'en-GB' : lang === 'fi' ? 'fi-FI' : 'sv-FI';
    const fmt = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
    return { lang, setLang, t: STRINGS[lang], num: (n) => fmt.format(n), locale };
  }, [lang, setLang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

